import * as React from 'react';
import { observer, inject } from 'mobx-react';
import { observable, computed, action } from 'mobx';
import { CSSTransitionGroup } from 'react-transition-group';
import { Modal } from 'components/content';
import { IAppOnlyStore } from 'stores/AllStores';
import { PricingRequestContent } from '.';
import {
  IPriceItem,
  IPricingRequestDetails,
  StatusOfNewPricingRequest,
} from 'common/interfaces/pricing';
import { LoadingFullScreen } from 'components/indicator';
import { unformat } from 'accounting';
import { UserType } from 'common/interfaces/user';

// Styles
const s = require('./PricingRequestModal.css');

// Props
interface IPricingRequestModalProps extends IAppOnlyStore {
  show: boolean;
  onModalClose: () => void;
  existingRequest?: {
    data: IPricingRequestDetails;
    status: StatusOfNewPricingRequest;
    onlyItems?: boolean;
    loading: boolean;
  };
}

// Component
@inject('store')
@observer
export class PricingRequestModal extends React.Component<
  IPricingRequestModalProps,
  {}
> {
  static get defaultProps(): IPricingRequestModalProps {
    return {
      show: false,
      onModalClose: null,
      existingRequest: null,
    };
  }

  constructor(props: IPricingRequestModalProps) {
    super(props);
  }

  @computed private get modalTitle(): string {
    const { existingRequest } = this.props;

    let str: string = '';

    if (existingRequest !== null) {
      if (
        typeof existingRequest.onlyItems !== 'undefined' &&
        existingRequest.onlyItems
      ) {
        str = 'Pricing';
      } else {
        if (this.props.store.user.userType === UserType.SalesManager) {
          str = 'Review pricing request';
          str +=
            existingRequest && existingRequest.data
              ? ' #' + existingRequest.data.id
              : '';
        } else {
          str = 'Pricing request';
          str +=
            existingRequest && existingRequest.data
              ? ' #' + existingRequest.data.id
              : '';
        }
      }
    } else {
      str = 'Create pricing request';
    }

    return str;
  }

  public render() {
    const { existingRequest } = this.props;
    const { newRequest } = this.props.store.leadPricing;

    return (
      <Modal
        show={this.props.show}
        title={this.modalTitle}
        titleBarContent={
          this.props.existingRequest === null
            ? this.renderPricingRequestTitleBarContent()
            : null
        }
        onModalClose={this.props.onModalClose}
      >
        {this.props.show && (
          <div className={s.content}>
            <PricingRequestContent
              onItemRemoveFromPricingRequest={
                this.handleItemRemoveFromPricingRequest
              }
              onItemRequestedQuantityChange={
                this.handleItemRequestedQuantityChange
              }
              onItemRequestedPriceChange={this.handleItemRequestedPriceChange}
              onItemApprovedPriceChange={this.handleItemApprovedPriceChange}
              onItemGroupPriceChange={this.handleItemGroupPriceChange}
              onRequestNoteChange={this.handleRequestNoteChange}
              onSubmitRequest={this.handleRequestSubmit}
              onApproveRequest={this.handleApproveRequest}
              onSaveRequest={this.handleSaveRequest}
              onUpdatePrices={this.handlePricesUpdate}
              existingRequest={
                this.props.existingRequest !== null
                  ? this.props.existingRequest.data
                  : null
              }
              userCanApprovePricing={
                this.props.store.user.userType === UserType.SalesManager
              }
              new={existingRequest === null ? true : false}
              onlyListItems={
                existingRequest !== null &&
                typeof existingRequest.onlyItems !== 'undefined'
                  ? existingRequest.onlyItems
                  : false
              }
              items={
                existingRequest !== null && existingRequest.data !== null
                  ? existingRequest.data.item.map((i) => {
                      return {
                        id: i.id,
                        isNew: false,
                        itemcode: i.itemcode,
                        itemname:
                          i.itemData !== null && i.itemData.itemname
                            ? i.itemData.itemname
                            : null,
                        price:
                          i.itemData !== null && i.itemData.price !== null
                            ? unformat(i.itemData.price)
                            : 0,
                        expectedPrice: i.expectedPrice,
                        approvedPrice: i.approvedPrice,
                        volume: i.volume,
                      };
                    })
                  : newRequest.items
              }
              groupItems={
                existingRequest !== null && existingRequest.data !== null
                  ? existingRequest.data.groupItems
                  : []
              }
              noteHtml={
                existingRequest !== null &&
                existingRequest.data !== null &&
                existingRequest.data.note
                  ? existingRequest.data.note
                  : newRequest.descriptionHtml
              }
              status={
                existingRequest !== null &&
                existingRequest.data !== null &&
                existingRequest.status !== null
                  ? existingRequest.status
                  : newRequest.status
              }
            />

            <LoadingFullScreen
              fitInContainer={true}
              active={
                this.props.existingRequest !== null &&
                typeof this.props.existingRequest.loading !== 'undefined' &&
                this.props.existingRequest.loading === true
              }
            />
          </div>
        )}
      </Modal>
    );
  }

  private renderPricingRequestTitleBarContent() {
    const {
      allPricingItemListLength,
      allPricingItemListLoading,
      newRequestItemsLength,
      pricingSearch,
      pricingSearchLength,
      pricingItemSearchResults,
      pricingItemSearchResultsLength,
    } = this.props.store.leadPricing;

    return (
      <div className={s.autocomplete}>
        <input
          ref={this.referenceSearchField}
          className={s.field}
          value={pricingSearch}
          placeholder="Search item code or name..."
          onChange={this.handleSearch}
          onFocus={this.toggleSearchFieldFocus(true)}
          onBlur={this.toggleSearchFieldFocus(false, 128)}
        />

        <CSSTransitionGroup
          component="div"
          className={s.resultsContainer}
          transitionName="fade"
          transitionAppear={true}
          transitionEnter={true}
          transitionLeave={true}
          transitionAppearTimeout={300}
          transitionEnterTimeout={300}
          transitionLeaveTimeout={300}
        >
          {allPricingItemListLoading === false && this.searchFieldFocused && (
            <div className={s.results}>
              {pricingItemSearchResults.map((result, i) => (
                <button
                  key={result.id}
                  className={s.result}
                  onClick={this.handleItemAddToPricingRequest(result)}
                >
                  <strong>{result.itemcode}</strong>
                  <span>{result.itemname}</span>
                </button>
              ))}
            </div>
          )}

          {pricingItemSearchResultsLength <= 0 && (
            <div key="no-items" className={s.results}>
              <p className={s.label}>
                {allPricingItemListLength <= 0
                  ? 'No items pricing found for this lead.'
                  : newRequestItemsLength >= allPricingItemListLength
                  ? 'All items have been added.'
                  : 'No items matching search query.'}
              </p>
            </div>
          )}

          {allPricingItemListLoading && (
            <div key="loading-items" className={s.results}>
              <p className={s.label}>Loading items...</p>
            </div>
          )}
        </CSSTransitionGroup>
      </div>
    );
  }

  private searchField: HTMLInputElement = null;
  private referenceSearchField = (ref: HTMLInputElement) =>
    (this.searchField = ref);

  @observable private searchFieldFocused: boolean = false;

  @action private toggleSearchFieldFocus = (
    focused?: boolean,
    delay?: number
  ) => (e: React.FocusEvent<HTMLInputElement>) => {
    setTimeout(
      () => {
        this.searchFieldFocused =
          typeof focused !== 'undefined' ? focused : !this.searchFieldFocused;
      },
      typeof delay !== 'undefined' ? delay : 0
    );
  };

  @action private handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.props.store.leadPricing.pricingSearch = e.currentTarget.value;
  };

  private handleItemAddToPricingRequest = (item: IPriceItem) => (
    e: React.MouseEvent<HTMLButtonElement>
  ) => {
    this.props.store.leadPricing.addItemToPriceRequest(item);
    this.props.store.leadPricing.pricingSearch = '';
  };

  private handleItemRemoveFromPricingRequest = (itemId: number) => {
    this.props.store.leadPricing.removeItemFromPriceRequest(itemId);
  };

  private handleItemApprovedPriceChange = (
    itemId: number,
    price: string,
    onlyListItems: boolean = false
  ) => {
    if (onlyListItems) {
      this.props.store.leadPricing.changeApprovedPriceOfLeadsItem(
        itemId,
        price
      );
    } else {
      this.props.store.pricingRequest.changeApprovedPriceOfSinglePricingRequest(
        itemId,
        price
      );
    }
  };

  private handleItemGroupPriceChange = (itemId: number, price: string) => {
    this.props.store.leadPricing.changeGroupPriceOfLeadsItem(itemId, price);
  };

  private handleItemRequestedPriceChange = (itemId: number, price: string) => {
    this.props.store.leadPricing.priceRequestItemChangePrice(itemId, price);
  };

  private handleItemRequestedQuantityChange = (
    itemId: number,
    quantity: string
  ) => {
    this.props.store.leadPricing.priceRequestItemChangeVolume(itemId, quantity);
  };

  private handleRequestNoteChange = (html: string, text: string) => {
    this.props.store.leadPricing.newRequest.descriptionHtml = html;
    this.props.store.leadPricing.newRequest.descriptionText = text;
  };

  private handleRequestSubmit = () => {
    if (this.props.store.leadPricing.newRequest.isNew) {
      this.props.store.leadPricing.createPricingRequest();
    } else {
      this.props.store.leadPricing.patchPricingRequest();
    }
  };

  private handleApproveRequest = () => {
    const { pricingRequest } = this.props.store;

    if (pricingRequest.singlePricingRequest !== null) {
      this.props.store.pricingRequest.approveSinglePricingRequest();
    }
  };

  private handleSaveRequest = () => {
    const { pricingRequest } = this.props.store;

    if (pricingRequest.singlePricingRequest !== null) {
      this.props.store.pricingRequest.saveSinglePricingRequestItemApprovedPricesChanges();
    }
  };

  private handlePricesUpdate = () => {
    this.props.store.leadPricing.saveUpdatedProviderAndGroupPrices();
  };
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./app/web/src/routes/sales/Lead/PricingRequest/Modal/PricingRequestModal.tsx